import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  SignalCellularAlt as SignalIcon,
  Router as RouterIcon,
  NetworkCheck as NetworkIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  SignalCellularOff as OfflineIcon,
  DeviceHub as DeviceHubIcon
} from '@mui/icons-material';
import OLTService, { PONData, PONStatistics, OLT } from '../services/oltService';
import ONUDataView from './ONUDataView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface PONDataViewProps {
  oltId: string;
  onError?: (error: string) => void;
}

const PONDataView: React.FC<PONDataViewProps> = ({ oltId, onError }) => {
  const [ponData, setPonData] = useState<PONData[]>([]);
  const [statistics, setStatistics] = useState<PONStatistics | null>(null);
  const [olt, setOlt] = useState<OLT | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedPonId, setSelectedPonId] = useState<string>('0/1/1');

  const fetchPonData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      setError(null);
      
      const response = await OLTService.getPonData(oltId);
      
      if (response.success) {
        setPonData(response.data.ponData);
        setStatistics(response.data.statistics);
        setOlt(response.data.olt);
        setLastUpdated(response.data.retrievedAt);
      } else {
        throw new Error(response.message || 'Failed to fetch PON data');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch PON data';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (oltId) {
      fetchPonData();
    }
  }, [oltId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      case 'critical':
        return <ErrorIcon fontSize="small" />;
      case 'inactive':
        return <OfflineIcon fontSize="small" />;
      default:
        return <ErrorIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getOnlinePercentage = (online: number, total: number) => {
    return total > 0 ? (online / total) * 100 : 0;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePonIdChange = (ponId: string) => {
    setSelectedPonId(ponId);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading PON data...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => fetchPonData()}
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header with OLT Info */}
      {olt && (
        <Card sx={{ mb: 2 }}>
          <CardHeader
            avatar={<RouterIcon />}
            title={`OLT ${olt.type} - ${olt.spop?.name || 'Unknown SPOP'}`}
            subheader={`${olt.ip_address}:${olt.port} (${olt.spop?.areaCode || 'N/A'})`}
            action={
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={olt.status}
                  color={olt.status === 'aktif' ? 'success' : 'default'}
                  size="small"
                />
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={() => fetchPonData(true)}
                    disabled={refreshing}
                  >
                    {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
          {lastUpdated && (
            <CardContent sx={{ pt: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Last updated: {formatDate(lastUpdated)}
              </Typography>
            </CardContent>
          )}
        </Card>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            }, 
            gap: 2 
          }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Ports
                </Typography>
                <Typography variant="h4">
                  {statistics.totalPorts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active: {statistics.activePorts}
                </Typography>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total ONUs
                </Typography>
                <Typography variant="h4">
                  {statistics.totalOnus}
                </Typography>
                <Box display="flex" alignItems="center" mt={1}>
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    Online: {statistics.totalOnline}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Port Health
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                  <Chip
                    label={`Healthy: ${statistics.healthyPorts}`}
                    color="success"
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                  <Chip
                    label={`Warning: ${statistics.warningPorts}`}
                    color="warning"
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                  <Chip
                    label={`Critical: ${statistics.criticalPorts}`}
                    color="error"
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Tabs for PON Overview and ONU Data */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="PON data tabs">
            <Tab 
              label="PON Overview" 
              icon={<SignalIcon />}
              iconPosition="start"
              id="simple-tab-0"
              aria-controls="simple-tabpanel-0"
            />
            <Tab 
              label="ONU Data" 
              icon={<DeviceHubIcon />}
              iconPosition="start"
              id="simple-tab-1"
              aria-controls="simple-tabpanel-1"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* PON Data Table */}
          <Card>
            <CardHeader
              title="PON Port Details"
              avatar={<SignalIcon />}
            />
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>PON ID</TableCell>
                      <TableCell align="center">Total ONUs</TableCell>
                      <TableCell align="center">Online</TableCell>
                      <TableCell align="center">Offline</TableCell>
                      <TableCell align="center">Online Rate</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Information</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ponData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No PON data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ponData.map((pon, index) => (
                        <TableRow 
                          key={index} 
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                          onClick={() => {
                            setSelectedPonId(pon.ponId);
                            setTabValue(1); // Switch to ONU data tab
                          }}
                        >
                          <TableCell>
                            <Typography variant="subtitle2" component="div">
                              {pon.ponId}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {pon.onuTotal}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="success.main">
                              {pon.onuOnline}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body2" 
                              color={pon.onuOffline > 0 ? "error.main" : "text.secondary"}
                            >
                              {pon.onuOffline}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <Typography variant="body2">
                                {getOnlinePercentage(pon.onuOnline, pon.onuTotal).toFixed(1)}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={getOnlinePercentage(pon.onuOnline, pon.onuTotal)}
                                color={pon.status === 'healthy' ? 'success' : pon.status === 'warning' ? 'warning' : 'error'}
                                sx={{ width: '60px', mt: 0.5 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              icon={getStatusIcon(pon.status)}
                              label={pon.status}
                              color={getStatusColor(pon.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {pon.information}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* ONU Data View Component */}
          <ONUDataView 
            oltId={oltId}
            ponId={selectedPonId}
            onPonIdChange={handlePonIdChange}
          />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default PONDataView;
